import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/cn";
import { useEffect, useId, useState, type MouseEvent, type RefObject } from "react";

interface TocEntry {
  id: string;
  text: string;
}

interface PostTocProps {
  /** Contêiner do corpo do post já renderizado (`PostBody`). */
  bodyRef: RefObject<HTMLElement | null>;
  /**
   * HTML do post. Não é usado para ler os títulos — serve de gatilho: quando
   * o post troca (navegação client-side entre artigos), o índice precisa ser
   * relido do DOM novo.
   */
  html: string;
  className?: string;
}

/**
 * Índice de conteúdo do artigo, montado a partir dos `h2`.
 *
 * Por que ler do DOM e não do HTML em string: o corpo do post entra por
 * `dangerouslySetInnerHTML`, então não existe árvore React para percorrer, e
 * parsear a string de novo aqui seria uma segunda verdade que dessincroniza
 * na primeira mudança do sanitizador. O efeito roda depois da montagem e lê os
 * `h2` que de fato estão na página, com o `id` que `addHeadingIds` já gravou
 * no HTML (cliente e SSR). Antes o `id` era escrito aqui, no DOM, e se perdia
 * quando o React reaplicava o `innerHTML` do corpo — o clique no índice mudava
 * a URL e não rolava.
 *
 * Em 360px o índice nasce **fechado**: seis títulos abertos empurrariam o
 * primeiro parágrafo para fora da primeira tela. A partir de 1024px — onde ele
 * passa a viver na coluna lateral — nasce aberto, porque ali há espaço de
 * sobra. O `<details>` é controlado por estado justamente para não reabrir
 * sozinho a cada rolagem, quando o item ativo muda e o componente re-renderiza.
 */
export const PostToc = ({ bodyRef, html, className }: PostTocProps) => {
  const tituloId = useId();
  const [entries, setEntries] = useState<TocEntry[]>([]);
  const [ativo, setAtivo] = useState<string | null>(null);
  const [aberto, setAberto] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );

  useEffect(() => {
    const raiz = bodyRef.current;
    if (!raiz) return;

    const encontrados: TocEntry[] = [];

    raiz.querySelectorAll("h2").forEach(h2 => {
      const texto = (h2.textContent ?? "").trim();
      // Sem `id` não há âncora: `addHeadingIds` só pula h2 vazio.
      if (!texto || !h2.id) return;
      encontrados.push({ id: h2.id, text: texto });
    });

    setEntries(encontrados);
    setAtivo(encontrados[0]?.id ?? null);
  }, [bodyRef, html]);

  // Marca no índice a seção que está sendo lida. É reforço: o item ativo tem
  // cor, peso e `aria-current`, nunca só cor.
  useEffect(() => {
    if (entries.length === 0) return;
    const raiz = bodyRef.current;
    if (!raiz) return;

    const alvos = entries
      .map(e => raiz.querySelector<HTMLElement>(`#${CSS.escape(e.id)}`))
      .filter((el): el is HTMLElement => el !== null);
    if (alvos.length === 0) return;

    const observer = new IntersectionObserver(
      registros => {
        const visivel = registros
          .filter(r => r.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visivel?.target.id) setAtivo(visivel.target.id);
      },
      // A faixa de detecção fica logo abaixo do cabeçalho fixo e ocupa o terço
      // superior da tela: é onde o olho está quando se lê rolando.
      { rootMargin: "-120px 0px -70% 0px", threshold: 0 }
    );

    alvos.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [bodyRef, entries]);

  /**
   * O clique rola até a seção aqui, e não pela navegação nativa da âncora: o
   * `href="#…"` vira uma navegação do React Router (`ScrollRestoration` no
   * `rootLayout`), que re-renderiza a página e engolia a rolagem — a URL
   * mudava e a tela ficava parada. O hash é gravado com `replaceState`
   * mantendo o `history.state` do router, para não criar navegação nem
   * entrada nova no histórico. Com Ctrl/Cmd/Shift o navegador segue no
   * comportamento padrão (abrir em outra aba, copiar o link).
   */
  const irPara = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const alvo = bodyRef.current?.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
    if (!alvo) return;

    event.preventDefault();
    const semMovimento = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    alvo.scrollIntoView({ behavior: semMovimento ? "auto" : "smooth", block: "start" });
    // Foco no título: quem navega por teclado ou leitor de tela continua a
    // leitura dali, e não do link do índice.
    alvo.setAttribute("tabindex", "-1");
    alvo.focus({ preventScroll: true });
    window.history.replaceState(window.history.state, "", `#${id}`);
    setAtivo(id);
  };

  // Menos de dois títulos não é índice — é um link só, e isso é ruído.
  if (entries.length < 2) return null;

  return (
    <nav
      aria-labelledby={tituloId}
      className={cn(
        "border-ds-border bg-ds-surface rounded-ds-lg border p-4",
        className
      )}
    >
      <details open={aberto} onToggle={e => setAberto(e.currentTarget.open)}>
        <summary
          id={tituloId}
          className="font-ds-sans text-ds-small text-ds-ink-900 rounded-ds-sm focus-visible:ring-ds-focus flex min-h-11 cursor-pointer list-none items-center font-semibold focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none [&::-webkit-details-marker]:hidden"
        >
          Neste artigo
          {/* Sem a seta, o `summary` fechado lê como um rótulo morto: nada na
              caixa diz que há uma lista ali dentro. */}
          <ChevronDown
            aria-hidden="true"
            size={16}
            className={cn(
              "text-ds-ink-600 ease-ds-out ml-auto transition-transform duration-150 motion-reduce:transition-none",
              aberto && "rotate-180"
            )}
          />
        </summary>

        <ol className="mt-2 flex flex-col">
          {entries.map((entry, i) => (
            <li key={entry.id}>
              <a
                href={`#${entry.id}`}
                onClick={event => irPara(event, entry.id)}
                aria-current={ativo === entry.id ? "location" : undefined}
                className={cn(
                  "font-ds-sans text-ds-small rounded-ds-sm focus-visible:ring-ds-focus flex min-h-11 cursor-pointer items-center gap-2 py-1 underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                  ativo === entry.id
                    ? "text-ds-brand-700 font-semibold"
                    : "text-ds-ink-700"
                )}
              >
                <span aria-hidden="true" className="ds-numeric text-ds-ink-600">
                  {i + 1}.
                </span>
                <span className="min-w-0">{entry.text}</span>
              </a>
            </li>
          ))}
        </ol>
      </details>
    </nav>
  );
};
